
import { Component, Vue, Prop } from 'vue-property-decorator';

@Component<TableHeaders>({})
export default class TableHeaders extends Vue {
	@Prop({ type: Boolean, required: false, default: false }) private noWrap!: boolean;
	@Prop({ type: Array, required: true, default: () => [] }) private headers!: TableHeader[];
	@Prop({ type: String, required: false, default: '' }) private initialSortColumn!: string;

	private currentSortColumn: string = '';
	private sortDirections: { [key: string]: 'asc' | 'desc' } = {};

	/**
	 * Initialize sort directions for all sortable headers.
	 *
	 * Note: `initialSortColumn` is read once on mount. If a parent needs to swap the
	 * active sort programmatically at runtime, remount the component with a `:key` that
	 * changes when the sort changes, or add a @Watch here.
	 *
	 * @return {void}
	 */
	private mounted(): void {
		this.headers.forEach(header => {
			if (header.sort && header.name) {
				this.sortDirections[header.name] = header.sort;
				// Set initial sort column if this header has a sort direction other than default
				if (header.sort && !this.currentSortColumn) {
					this.currentSortColumn = header.name;
				}
			}
		});

		// Parent-provided override takes precedence over first-sortable fallback
		if (this.initialSortColumn) {
			this.currentSortColumn = this.initialSortColumn;
		}
	}

	/**
	 * Handle sort toggle for a column
	 *
	 * @param {string} columnName
	 * @return {void}
	 */
	private toggleSort(columnName: string): void {
		if (this.currentSortColumn === columnName) {
			// Toggle direction for current column
			this.sortDirections[columnName] =
				this.sortDirections[columnName] === 'asc' ? 'desc' : 'asc';
		}
		else {
			// Switch to new column
			this.currentSortColumn = columnName;
		}

		this.$emit('toggle-sort', columnName);
	}

	/**
	 * Get the appropriate sort icon based on column state
	 *
	 * @param {string} columnName
	 * @return {string}
	 */
	private getSortIcon(columnName: string): string {
		if (this.currentSortColumn !== columnName) {
			return 'mdi-sort';
		}

		return this.sortDirections[columnName] === 'asc' ? 'mdi-sort-ascending' : 'mdi-sort-descending';
	}

	/**
	 * Check if a column is currently being sorted
	 *
	 * @param {string} columnName
	 * @return {boolean}
	 */
	private isColumnActive(columnName: string): boolean {
		return this.currentSortColumn === columnName;
	}
}
